import { useEffect, useState } from "react";
import { login, register, requestPasswordReset, resetPassword } from "../api";
import { CompanyLogo } from "../components/common";
import {
  getInitialContractorToken,
  getInitialResetToken,
} from "../utils/loginParams";

const LoginView = ({ onAuthenticated }) => {
  const initialContractorToken = getInitialContractorToken();
  const [form, setForm] = useState({ username: "", password: "" });
  const [registerForm, setRegisterForm] = useState(() => ({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    kontrahentIdHttp: initialContractorToken,
    password: "",
    confirmPassword: "",
  }));
  const initialResetToken = getInitialResetToken();
  const [mode, setMode] = useState(() => {
    if (initialResetToken) {
      return "reset";
    }
    if (initialContractorToken) {
      return "register";
    }
    return "login";
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetForm, setResetForm] = useState({
    token: initialResetToken,
    password: "",
    confirmPassword: "",
  });
  const [hasTokenFromLink, setHasTokenFromLink] = useState(
    Boolean(initialResetToken),
  );
  const [idHttpInput, setIdHttpInput] = useState("");

  useEffect(() => {
    if (initialResetToken) {
      setResetForm((prev) => ({
        ...prev,
        token: initialResetToken,
      }));
      setHasTokenFromLink(true);
      setMode("reset");
    }
  }, [initialResetToken]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegisterChange = (event) => {
    const { name, value } = event.target;
    setRegisterForm((prev) => ({ ...prev, [name]: value }));
  };

  const switchMode = (
    nextMode: string,
    options: { email?: string; token?: string } = {},
  ) => {
    setMode(nextMode);
    setError("");
    setSuccess("");
    setLoading(false);
    if (nextMode === "forgot") {
      setForgotEmail(options.email || "");
    }
    if (nextMode === "reset") {
      const resolvedToken = options.token ?? resetForm.token;
      setResetForm(() => ({
        token: resolvedToken,
        password: "",
        confirmPassword: "",
      }));
      setHasTokenFromLink(Boolean(resolvedToken));
    }
  };

  const isValidUUID = (uuid: string): boolean => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const user = await login(form);
      onAuthenticated(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!registerForm.kontrahentIdHttp) {
      setError("Brakuje parametru Kontrahent_IdHTTP w linku rejestracyjnym.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        email: registerForm.email.trim(),
        password: registerForm.password,
        confirmPassword: registerForm.confirmPassword,
        firstName: registerForm.firstName.trim(),
        lastName: registerForm.lastName.trim(),
        phone: registerForm.phone.trim(),
        kontrahentIdHttp: registerForm.kontrahentIdHttp.trim(),
      };
      await register(payload);
      setSuccess("Konto utworzone. Możesz teraz zalogować się do systemu.");
      setRegisterForm((prev) => ({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        kontrahentIdHttp: prev.kontrahentIdHttp,
        password: "",
        confirmPassword: "",
      }));
      setMode("login");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!forgotEmail.trim()) {
      setError("Podaj adres e-mail powiązany z kontem.");
      return;
    }
    setLoading(true);
    try {
      const response = await requestPasswordReset(forgotEmail.trim());
      setSuccess(
        response?.message ||
          "Jeżeli konto istnieje, wysłaliśmy instrukcje resetu hasła.",
      );
      setForgotEmail("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetChange = (event) => {
    const { name, value } = event.target;
    setResetForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const trimmedToken = resetForm.token.trim();
    if (resetForm.password.length < 8) {
      setError("Hasło musi mieć co najmniej 8 znaków.");
      return;
    }
    if (resetForm.password !== resetForm.confirmPassword) {
      setError("Hasła muszą być identyczne.");
      return;
    }

    setLoading(true);
    try {
      const response = await resetPassword({
        token: trimmedToken,
        password: resetForm.password,
      });
      const message =
        response?.message ||
        "Hasło zostało zmienione. Możesz się teraz zalogować.";
      setResetForm((prev) => ({
        ...prev,
        password: "",
        confirmPassword: "",
      }));
      setHasTokenFromLink(true);
      switchMode("login");
      setSuccess(message);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderLogin = () => (
    <form onSubmit={handleSubmit} className="form">
      <label htmlFor="login-email">
        <span>Adres e-mail</span>
        <input
          id="login-email"
          type="email"
          name="username"
          autoComplete="email"
          value={form.username}
          onChange={handleChange}
          required
        />
      </label>
      <label htmlFor="login-password">
        <span>Hasło</span>
        <input
          id="login-password"
          type="password"
          name="password"
          autoComplete="current-password"
          value={form.password}
          onChange={handleChange}
          required
        />
      </label>
      {error && <div className="alert">{error}</div>}
      {success && <div className="alert success">{success}</div>}
      <button type="submit" disabled={loading} className="primary">
        {loading ? "Logowanie..." : "Zaloguj się"}
      </button>
      <button
        type="button"
        className="link-button"
        onClick={() => switchMode("forgot")}
      >
        Nie pamiętasz hasła?
      </button>
    </form>
  );

  const renderRegister = () => {
    // Step 1: Ask for IdHTTP if not provided
    if (!registerForm.kontrahentIdHttp) {
      return (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmedId = idHttpInput.trim();

            if (!trimmedId) {
              setError("Podaj identyfikator kontrahenta.");
              return;
            }

            if (!isValidUUID(trimmedId)) {
              setError(
                "Nieprawidłowy format identyfikatora. Powinien być: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX",
              );
              return;
            }

            setRegisterForm((prev) => ({
              ...prev,
              kontrahentIdHttp: trimmedId,
            }));
            setError("");
          }}
          className="form"
        >
          <div className="registration-info">
            <h3>Nie masz linku rejestracyjnego?</h3>
            <p>Skontaktuj się z zespołem sprzedaży Kropek:</p>
            <div className="contact-info">
              <div className="contact-item">
                <strong>Telefon:</strong> +48 500 123 456
              </div>
              <div className="contact-item">
                <strong>E-mail:</strong> b2b@kropek.pl
              </div>
              <div className="contact-item">
                <strong>Godziny pracy:</strong> Pon - Pt 7:00-15:00
              </div>
            </div>
          </div>

          <label htmlFor="register-idhttpinput">
            <span>Identyfikator kontrahenta (ID HTTP)</span>
            <input
              id="register-idhttpinput"
              type="text"
              name="idHttpInput"
              autoComplete="off"
              placeholder="Wklej identyfikator z otrzymanego linku"
              value={idHttpInput}
              onChange={(e) => {
                const value = e.target.value;
                setIdHttpInput(value);
                // Live validation
                if (value.trim() && !isValidUUID(value.trim())) {
                  setError(
                    "Nieprawidłowy format. Powinien być: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX",
                  );
                } else {
                  setError("");
                }
              }}
              required
            />
          </label>
          <p className="form-hint">
            Identyfikator ma format: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
          </p>
          {error && <div className="alert">{error}</div>}
          {success && <div className="alert success">{success}</div>}
          <button
            type="submit"
            className="primary"
            disabled={!idHttpInput.trim() || !isValidUUID(idHttpInput.trim())}
          >
            Dalej
          </button>
        </form>
      );
    }

    // Step 2: Full registration form
    return (
      <form onSubmit={handleRegister} className="form">
        <label htmlFor="register-firstname">
          <span>Imię</span>
          <input
            id="register-firstname"
            type="text"
            name="firstName"
            autoComplete="given-name"
            value={registerForm.firstName}
            onChange={handleRegisterChange}
            required
          />
        </label>
        <label htmlFor="register-lastname">
          <span>Nazwisko</span>
          <input
            id="register-lastname"
            type="text"
            name="lastName"
            autoComplete="family-name"
            value={registerForm.lastName}
            onChange={handleRegisterChange}
            required
          />
        </label>
        <label htmlFor="register-email">
          <span>Adres e-mail</span>
          <input
            id="register-email"
            type="email"
            name="email"
            autoComplete="email"
            value={registerForm.email}
            onChange={handleRegisterChange}
            required
          />
        </label>
        <label htmlFor="register-phone">
          <span>Telefon</span>
          <input
            id="register-phone"
            type="tel"
            name="phone"
            autoComplete="tel"
            value={registerForm.phone}
            onChange={handleRegisterChange}
          />
        </label>
        <label htmlFor="register-password">
          <span>Hasło</span>
          <input
            id="register-password"
            type="password"
            name="password"
            autoComplete="new-password"
            value={registerForm.password}
            onChange={handleRegisterChange}
            required
            minLength={8}
          />
        </label>
        <label htmlFor="register-confirm-password">
          <span>Potwierdź hasło</span>
          <input
            id="register-confirm-password"
            type="password"
            name="confirmPassword"
            autoComplete="new-password"
            value={registerForm.confirmPassword}
            onChange={handleRegisterChange}
            required
            minLength={8}
          />
        </label>
        <p className="form-hint">Hasło musi mieć co najmniej 8 znaków.</p>
        {error && <div className="alert">{error}</div>}
        {success && <div className="alert success">{success}</div>}
        <button type="submit" disabled={loading} className="primary">
          {loading ? "Rejestracja..." : "Utwórz konto"}
        </button>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            setRegisterForm((prev) => ({
              ...prev,
              kontrahentIdHttp: "",
            }));
            setIdHttpInput("");
            setError("");
            setSuccess("");
          }}
        >
          Zmień identyfikator
        </button>
      </form>
    );
  };

  const renderForgot = () => (
    <form onSubmit={handleForgotSubmit} className="form">
      <label htmlFor="forgot-email">
        <span>Adres e-mail</span>
        <input
          id="forgot-email"
          type="email"
          name="forgotEmail"
          autoComplete="email"
          value={forgotEmail}
          onChange={(event) => setForgotEmail(event.target.value)}
          required
        />
      </label>
      <p className="form-hint">
        Wyślemy na podany adres link pozwalający ustawić nowe hasło.
      </p>
      {error && <div className="alert">{error}</div>}
      {success && <div className="alert success">{success}</div>}
      <button type="submit" className="primary" disabled={loading}>
        {loading ? "Wysyłanie..." : "Wyślij instrukcje"}
      </button>
      <button
        type="button"
        className="link-button"
        onClick={() => switchMode("login")}
      >
        ← Wróć do logowania
      </button>
    </form>
  );

  const renderReset = () => (
    <form onSubmit={handleResetSubmit} className="form">
      {hasTokenFromLink && (
        <input
          type="hidden"
          id="reset-token-hidden"
          name="token"
          value={resetForm.token}
        />
      )}
      {!hasTokenFromLink && (
        <label htmlFor="reset-token">
          <span>Token resetu</span>
          <input
            id="reset-token"
            type="text"
            name="token"
            autoComplete="off"
            value={resetForm.token}
            onChange={handleResetChange}
            required
          />
        </label>
      )}
      <label htmlFor="reset-password">
        <span>Nowe hasło</span>
        <input
          id="reset-password"
          type="password"
          name="password"
          autoComplete="new-password"
          value={resetForm.password}
          onChange={handleResetChange}
          required
          minLength={8}
        />
      </label>
      <label htmlFor="reset-confirm-password">
        <span>Powtórz nowe hasło</span>
        <input
          id="reset-confirm-password"
          type="password"
          name="confirmPassword"
          autoComplete="new-password"
          value={resetForm.confirmPassword}
          onChange={handleResetChange}
          required
          minLength={8}
        />
      </label>
      {error && <div className="alert">{error}</div>}
      {success && <div className="alert success">{success}</div>}
      <button type="submit" className="primary" disabled={loading}>
        {loading ? "Zapisywanie..." : "Ustaw nowe hasło"}
      </button>
      <button
        type="button"
        className="link-button"
        onClick={() => switchMode("login")}
      >
        ← Wróć do logowania
      </button>
    </form>
  );

  const renderActiveForm = () => {
    if (mode === "login") return renderLogin();
    if (mode === "register") return renderRegister();
    if (mode === "forgot") return renderForgot();
    return renderReset();
  };

  const renderSubtitle = () => {
    if (mode === "register") {
      if (!registerForm.kontrahentIdHttp) {
        return "Aby się zarejestrować, potrzebujesz identyfikatora kontrahenta.";
      }
      return "Zarejestruj konto powiązane z kontrahentem Kropek.";
    }
    if (mode === "forgot") {
      return "Podaj adres e-mail, aby otrzymać link resetujący hasło.";
    }
    if (mode === "reset") {
      return "Ustaw nowe hasło dla swojego konta.";
    }
    return "Zaloguj się, aby złożyć zamówienie.";
  };

  const showSwitch = mode === "login" || mode === "register";

  return (
    <div className="app-shell">
      <div className="card login-card">
        <CompanyLogo size="large" />
        <p className="subtitle">{renderSubtitle()}</p>
        {showSwitch ? (
          <div className="form-switch">
            <button
              type="button"
              className={mode === "login" ? "active" : ""}
              onClick={() => switchMode("login")}
            >
              Logowanie
            </button>
            <button
              type="button"
              className={mode === "register" ? "active" : ""}
              onClick={() => switchMode("register")}
            >
              Rejestracja
            </button>
          </div>
        ) : null}
        {renderActiveForm()}
        <p className="version-info">Kropek B2B v2.2.5</p>
      </div>
    </div>
  );
};

export default LoginView;
